import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * CTO Directive #121.4 — ME Physical Presence Score.
 *
 * The Living Orb no longer *claims* presence. This component reads
 * /api/me/voice/physical-presence and renders the truth:
 *
 *   • a numeric 0..100 grounded score
 *   • one icon per organ (mic / speaker / screen / interaction)
 *
 * We poll every 2.5 s so the UI stays in sync with real device
 * events but never spins.
 */

const ICONS = {
  microphone: (
    // simple mic glyph
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  ),
  speaker: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10v4h4l5 4V6l-5 4H4z" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19 6a8 8 0 0 1 0 12" />
    </svg>
  ),
  screen: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8" />
      <path d="M12 16v4" />
    </svg>
  ),
  interaction: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20l-6-6h4V4h4v10h4z" transform="rotate(-45 12 12)" />
    </svg>
  ),
};

function scoreTone(score) {
  if (score >= 70) return "text-emerald-300 border-emerald-400/40";
  if (score >= 40) return "text-sky-300 border-sky-400/40";
  if (score > 0) return "text-amber-300 border-amber-400/40";
  return "text-slate-500 border-slate-600/40";
}

export default function PhysicalPresenceScore({
  language = "tr",
  pollingMs = 2500,
}) {
  const [snap, setSnap] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let stopped = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/me/voice/physical-presence");
        if (!stopped && mounted.current) setSnap(data);
      } catch (_) { /* silent — the UI stays on the last known truth */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, Math.max(1000, pollingMs));
    return () => { stopped = true; clearInterval(t); };
  }, [pollingMs]);

  if (!snap) return null;
  const score = Number.isFinite(snap.score) ? snap.score : 0;
  const axes = snap.axes || {};
  const reason = language === "tr" ? snap.reason_tr : snap.reason_en;
  const tone = scoreTone(score);

  // Organ badges are driven directly by axes so state and UI can
  // never drift.
  const badges = [
    { axis: "microphone",  active: !!axes.microphone_listening,               label: language === "tr" ? "Mikrofon" : "Mic" },
    { axis: "speaker",     active: !!(axes.audio_playback_technical_verified || axes.audio_output_user_confirmed), label: language === "tr" ? "Hoparlör" : "Speaker" },
    { axis: "screen",      active: !!axes.trusted_display_available,          label: language === "tr" ? "Ekran"    : "Display" },
    { axis: "interaction", active: !!axes.recent_interaction,                 label: language === "tr" ? "Etkileşim": "Interaction" },
  ];

  // CTO #122.1.2 §Preview Dürüstlüğü — surface the work verification
  // mode explicitly so the UI never lets the user mistake a
  // simulated apply for a real one.
  const mode = snap.work_verification_mode || "none";
  const modeLabel = language === "tr"
    ? { real: "GERÇEK", simulated: "SİMÜLASYON", none: null }[mode]
    : { real: "REAL", simulated: "SIMULATED", none: null }[mode];

  return (
    <div
      className="mt-3 flex flex-col items-center gap-1"
      data-testid="me-physical-presence"
    >
      <div className="flex items-center gap-2">
        <span
          className={`mono inline-flex items-baseline gap-1 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] backdrop-blur ${tone}`}
          data-testid="physical-presence-score"
          title={reason}
        >
          <span className="opacity-70">
            {language === "tr" ? "varlık" : "presence"}
          </span>
          <span className="mono text-[12px] font-medium tabular-nums">
            {score}
          </span>
        </span>
        <div
          className="flex items-center gap-1"
          data-testid="physical-presence-organs"
        >
          {badges.map((b) => (
            <span
              key={b.axis}
              data-testid={`presence-organ-${b.axis}`}
              data-active={b.active ? "1" : "0"}
              title={b.label}
              className={
                "inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors " +
                (b.active
                  ? "border-sky-300/60 bg-sky-300/[0.08] text-sky-200"
                  : "border-slate-700/60 bg-slate-900/40 text-slate-600")
              }
            >
              {ICONS[b.axis]}
            </span>
          ))}
        </div>
      </div>
      <span className="text-[10px] italic text-slate-500">
        {reason}
      </span>
      {modeLabel && (
        <span
          className={
            "mono text-[9px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full border " +
            (mode === "real"
              ? "border-emerald-400/40 text-emerald-300"
              : "border-amber-400/40 text-amber-300")
          }
          data-testid="work-verification-mode"
          data-mode={mode}
          title={
            mode === "simulated"
              ? (language === "tr"
                  ? "Bu ortamda işlemi canlandırıyorum; gerçek Windows dosya değişikliği yalnızca kurulu MEOS uygulamasında doğrulanabilir."
                  : "Simulated in preview; real Windows file changes are only verifiable in the installed MEOS app.")
              : (language === "tr"
                  ? "Gerçek Windows adapter doğrulaması."
                  : "Real Windows adapter verification.")
          }
        >
          {modeLabel}
        </span>
      )}
    </div>
  );
}
